import { createRoot } from 'react-dom/client';
// Roboto ships with the bundle instead of coming from fonts.googleapis.com:
// no third-party request on a HIPAA app, and the CSP no longer needs the
// Google hosts (ZAP flagged the cross-origin stylesheet without SRI).
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'index.css';
import AppRoot from './app/AppRoot';
import { sweepFilterHistoryIfSignedOut } from '@shared/lib/filterHistory';
import { sweepTableFiltersIfSignedOut } from '@shared/lib/tableFilters';

// Opened without a session (closed without logging out, timed out while
// the browser was shut): drop the last session's table filters and their
// history.
sweepFilterHistoryIfSignedOut();
sweepTableFiltersIfSignedOut();

// Suppress ResizeObserver errors in development
if (process.env.NODE_ENV === 'development') {
  const debounce = (callback, delay) => {
    let tid;
    return function (...args) {
      const ctx = this;
      clearTimeout(tid);
      tid = setTimeout(() => callback.apply(ctx, args), delay);
    };
  };

  const errorHandler = debounce(e => {
    if (
      e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
      e.message === 'ResizeObserver loop limit exceeded'
    ) {
      e.stopImmediatePropagation();
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  }, 0);

  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', e => {
    if (
      e.reason &&
      e.reason.message &&
      (e.reason.message.includes('ResizeObserver loop completed') ||
        e.reason.message.includes('ResizeObserver loop limit'))
    ) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  });
}

async function enableMockingIfRequested() {
  if (process.env.REACT_APP_USE_MOCKS !== 'true') return;
  const { worker } = await import('./mocks/browser');
  await worker.start({
    onUnhandledRequest: 'bypass',
    serviceWorker: { url: '/mockServiceWorker.js' },
  });
}

const container = document.getElementById('root');
const root = createRoot(container);

enableMockingIfRequested()
  .catch(error => {
    // A worker.start rejection (e.g. service worker registration failure)
    // must not block the app from rendering — mocks are a dev-only
    // convenience, not a hard dependency.
    console.error('Mock service worker failed to start:', error);
  })
  .then(() => {
    root.render(<AppRoot />);
  });
